import { CalendarOutlined, CheckOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { lunchPeriodText, lunchScheduleText, type LunchDraft } from "../lunchDraft";
import { useLunchProducts } from "../useLunchProducts";
import { LunchSelectedRow } from "./LunchSelectedRow";
import { KbdChip } from "../../components/modalPrimitives";

interface LunchMenuPanelProps {
  draft: LunchDraft;
  saving: boolean;
  onEditSchedule: () => void;
  onEditItem: (itemId: number) => void;
  onCancel: () => void;
  onSave: () => void;
  /** В bottom sheet-а (под десктоп) — без собствена рамка/ширина */
  inSheet?: boolean;
}

/** Черновата на обедното меню: име, график, избраните продукти и Запази. */
export const LunchMenuPanel = ({
  draft,
  saving,
  onEditSchedule,
  onEditItem,
  onCancel,
  onSave,
  inSheet = false,
}: LunchMenuPanelProps) => {
  const { t } = useTranslation();
  const productOf = useLunchProducts();
  const schedule = {
    dateFrom: draft.dateFrom,
    dateTo: draft.mode === "single" ? draft.dateFrom : draft.dateTo,
    weekdays: draft.mode === "recurring" ? draft.weekdays : null,
    timeFrom: draft.timeFrom,
    timeTo: draft.timeTo,
  };
  const period = lunchPeriodText(t, schedule);

  return (
    <div
      className={`flex min-h-0 flex-col ${
        inSheet ? "flex-1" : "w-[clamp(300px,27%,388px)] shrink-0 border-l border-pos-line bg-pos-panel"
      }`}
    >
      <div className="flex flex-col gap-3 border-b border-pos-line2 px-4 pb-3 pt-4">
        <div className="flex flex-col gap-0.5">
          <span className="font-pos-mono text-[11px] font-semibold uppercase tracking-[.1em] text-pos-sec-head">
            {t("lunchMenu.editorKicker")}
          </span>
          <span className="truncate text-lg font-extrabold leading-[1.2] text-pos-ink">
            {draft.name || "—"}
          </span>
        </div>
        <button
          type="button"
          onClick={onEditSchedule}
          className="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-pos-line3 bg-pos-card px-3 py-2.5 text-left transition-colors hover:border-pos-accent/40"
        >
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="flex items-center gap-2 text-[15px] font-semibold text-pos-ink2">
              <CalendarOutlined />
              <span className="truncate font-pos-mono">{lunchScheduleText(t, schedule)}</span>
            </span>
            <span className="flex items-center gap-2 text-sm text-pos-soft2">
              <ClockCircleOutlined />
              <span className="truncate">
                {draft.mode === "single" ? t("lunchMenu.scheduleSingleDate") : period}
              </span>
            </span>
          </span>
          <span className="text-[13px] font-bold text-pos-accent-ink">
            {t("lunchMenu.scheduleChange")}
          </span>
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-4 py-3">
        <div className="mb-0.5 flex items-center justify-between font-pos-mono text-[11px] font-semibold uppercase tracking-[.08em] text-pos-dim">
          <span>
            {t("lunchMenu.products")} · {draft.items.length}
          </span>
          <span className="normal-case tracking-normal">{t("lunchMenu.priceColumnsHint")}</span>
        </div>
        {draft.items.length === 0 ? (
          <p className="m-0 py-6 text-center text-[13px] text-pos-muted">
            {t(inSheet ? "lunchMenu.emptySelectionNarrow" : "lunchMenu.emptySelection")}
          </p>
        ) : (
          draft.items.map((draftItem) => (
            <LunchSelectedRow
              key={draftItem.itemId}
              draftItem={draftItem}
              product={productOf(draftItem.itemId)}
              onEdit={onEditItem}
              inSheet={inSheet}
            />
          ))
        )}
      </div>

      <div className="flex gap-2 border-t border-pos-line px-4 pb-[calc(16px+env(safe-area-inset-bottom,0px)*0.5)] pt-3">
        <button
          type="button"
          onClick={onCancel}
          className="relative inline-flex h-[50px] cursor-pointer items-center justify-center rounded-[13px] border border-pos-ctl bg-pos-card px-[18px] text-[15px] font-semibold text-pos-ink2 transition-colors hover:bg-pos-panel"
        >
          {t("common.cancel")}
          {!inSheet && <KbdChip shortcut="Esc" colorClass="text-pos-faint" />}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="relative inline-flex h-[50px] flex-1 cursor-pointer items-center justify-center gap-2 rounded-[13px] border-0 bg-pos-accent text-[15px] font-bold text-white transition-colors hover:bg-pos-accent-ink disabled:cursor-not-allowed disabled:opacity-60"
        >
          <CheckOutlined />
          {t("lunchMenu.save")}
          {!inSheet && <KbdChip shortcut="⏎" colorClass="text-white/60" />}
        </button>
      </div>
    </div>
  );
};
